import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Network } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGlobal } from "@/context/GlobalContext";
import { weekOf } from "@/lib/semanas";
import { WeekPicker } from "@/components/control/WeekPicker";
import { ReporteEditor } from "@/components/control/ReporteEditor";
import { useUnsavedGuard } from "@/pages/PreciosCompra";

const MSG = "El RPT Avance está disponible únicamente a nivel de planta o sucursal. Seleccione una planta o sucursal desde la estructura organizacional para visualizar su información.";

function Aviso({ text, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm py-16 px-6 flex flex-col items-center text-center gap-3" data-testid={testid}>
      <Network className="w-8 h-8 text-red-500" />
      <p className="max-w-xl text-sm text-slate-600">{text}</p>
    </div>
  );
}

// Mantiene el nodo mostrado; si hay cambios sin guardar, pide confirmación antes de cambiar o restaura el nodo anterior.
function useShownNode(guard) {
  const { structureKey, node, setNode } = useGlobal();
  const [shown, setShown] = useState({ structureKey, node });
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    const prev = shownRef.current;
    if (prev.structureKey === structureKey && (prev.node?.id ?? null) === (node?.id ?? null)) return;
    guard(() => setShown({ structureKey, node }), () => { if (prev.structureKey === structureKey) setNode(prev.node); else setShown({ structureKey, node }); });
  }, [structureKey, node, guard, setNode]);
  return shown;
}

export default function ReporteControl() {
  const { user } = useAuth();
  const { currentStructure } = useGlobal();
  const canEdit = user?.is_admin || (user?.caps || []).includes("reporte_control_cap");
  const [yw, setYWRaw] = useState(weekOf());
  const { onDirtyChange, guard, dialog } = useUnsavedGuard();
  const setYW = (v) => guard(() => setYWRaw(v));
  const { structureKey, node } = useShownNode(guard);
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "nodo", structureKey, node?.id ?? "entire"],
    queryFn: async () => (await api.get("/control-semanal/reporte/nodo", { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
  });
  let body;
  if (isLoading) body = <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  else if (error) body = <Aviso testid="reporte-error" text={formatApiErrorDetail(error?.response?.data?.detail) || "No se pudo cargar el nodo seleccionado."} />;
  else if (!data?.is_leaf) body = <Aviso testid="reporte-no-hoja" text={MSG} />;
  else body = (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm">
      <ReporteEditor key={`${data.planta}-${yw.year}-${yw.week}`} planta={data.planta} year={yw.year} week={yw.week} structure={structureKey} canEdit={canEdit} onDirtyChange={onDirtyChange} />
    </div>
  );
  return (
    <div className="w-full space-y-6" data-testid="reporte-control">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Control Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">RPT Avance</h1>
        <p className="text-sm text-slate-500 mt-1">Presupuesto y rentabilidad semanal: planeado contra real ejecutado. {currentStructure?.name ? `Estructura: ${currentStructure.name}${node ? ` · ${node.name}` : " · Toda la estructura"}` : ""}</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <WeekPicker year={yw.year} week={yw.week} setYW={setYW} />
      </div>
      {body}
      {dialog}
    </div>
  );
}
